import {
  getCheckoutUrls,
  SUBSCRIPTION_TIERS,
  SubscriptionStatus,
  SubscriptionTierId,
  useCreateCheckout,
  useCreatePortalSession,
} from '../hooks/useCheckout';

interface SubscriptionCardProps {
  subscription?: SubscriptionStatus;
  enabled?: boolean;
}

export default function SubscriptionCard({ subscription, enabled = true }: SubscriptionCardProps) {
  const createCheckout = useCreateCheckout();
  const createPortalSession = useCreatePortalSession();
  const checkoutUrls = getCheckoutUrls();

  const hasActiveSubscription = subscription?.hasSubscription && subscription.status !== 'canceled';

  const handleSubscribe = (tierId: SubscriptionTierId) => {
    createCheckout.mutate({
      productType: 'subscription',
      productId: tierId,
      ...checkoutUrls,
    });
  };

  // Coming soon state when subscriptions are disabled
  if (!enabled) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-purple-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
            />
          </svg>
          Subscription
        </h2>
        <div className="text-center py-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600/20 text-purple-300 rounded-full text-sm font-medium">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            Coming Soon
          </div>
          <p className="mt-4 text-purple-300/70 text-sm">
            Subscription plans will be available soon. Stay tuned!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
        <svg
          className="w-5 h-5 text-purple-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
          />
        </svg>
        Subscription
      </h2>

      {/* Subscription tiers */}
      <div className="space-y-3">
        {SUBSCRIPTION_TIERS.map((tier) => {
          const isCurrent = subscription?.tier === tier.id;

          return (
            <div
              key={tier.id}
              className={`relative p-4 rounded-xl border transition-all ${
                isCurrent
                  ? 'bg-purple-600/10 border-purple-500/30'
                  : 'bg-slate-700/30 border-white/10'
              }`}
            >
              {'popular' in tier && tier.popular && !isCurrent && (
                <span className="absolute -top-2 right-3 px-2 py-0.5 bg-purple-600 text-white text-xs font-medium rounded">
                  Popular
                </span>
              )}

              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-white font-medium">{tier.name}</span>
                    {isCurrent && (
                      <span
                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                          subscription?.status === 'active'
                            ? 'bg-green-500/20 text-green-400'
                            : subscription?.status === 'past_due'
                              ? 'bg-yellow-500/20 text-yellow-400'
                              : 'bg-red-500/20 text-red-400'
                        }`}
                      >
                        {subscription?.status === 'active'
                          ? 'Active'
                          : subscription?.status === 'past_due'
                            ? 'Past Due'
                            : 'Canceled'}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-purple-300">{tier.tokensPerMonth} tokens/month</div>
                  <div className="text-sm text-purple-400">{tier.description}</div>
                  {isCurrent &&
                    subscription?.currentPeriodEnd &&
                    (() => {
                      const periodEnd = new Date(subscription.currentPeriodEnd);
                      const isPast = periodEnd < new Date();
                      const formattedDate = periodEnd.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                      });

                      // Determine the label based on status and timing
                      let label: string;
                      if (subscription.status === 'canceled') {
                        label = isPast ? 'Ended' : 'Ending';
                      } else if (subscription.pendingTier) {
                        const pendingTierName = SUBSCRIPTION_TIERS.find(
                          (t) => t.id === subscription.pendingTier
                        )?.name;
                        label = pendingTierName
                          ? `Downgrades to ${pendingTierName} on`
                          : 'Downgrades on';
                      } else if (subscription.cancelAtPeriodEnd) {
                        label = 'Expires';
                      } else {
                        label = 'Renews';
                      }

                      return (
                        <div className="text-xs text-purple-400/70 mt-1">
                          {label} {formattedDate}
                        </div>
                      );
                    })()}
                </div>

                <div className="text-right">
                  <div className="text-white font-semibold">{tier.price}</div>
                  {/* Only show Subscribe button if user has no active subscription */}
                  {!hasActiveSubscription && (
                    <button
                      onClick={() => handleSubscribe(tier.id)}
                      disabled={createCheckout.isPending}
                      className="mt-2 px-4 py-1.5 bg-purple-600 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors disabled:opacity-50"
                    >
                      Subscribe
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Manage Subscription button for existing subscribers with Stripe customer ID */}
      {hasActiveSubscription && subscription?.stripeCustomerId && (
        <button
          onClick={() => createPortalSession.mutate()}
          disabled={createPortalSession.isPending}
          className="mt-4 w-full py-2.5 px-4 bg-slate-700/50 hover:bg-slate-700 text-purple-300 hover:text-white text-sm rounded-lg transition-colors disabled:opacity-50"
        >
          {createPortalSession.isPending ? 'Loading...' : 'Manage Subscription'}
        </button>
      )}

      {createCheckout.isPending && (
        <p className="text-sm text-purple-300 mt-4 text-center">Redirecting to checkout...</p>
      )}
    </div>
  );
}
